import React, { useMemo } from "react";
import { API_Q_MEDALS_IMAGE } from "../../../constants/api"
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as routes from "../../../constants/routes";
import DashboardCard from "../DashboardCard";

const MedalsGrid = styled(Box)({
  display: "flex",
  gap: "12px",
  overflowX: "auto",
  paddingBottom: "8px",
  userSelect: "none",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
});

const MedalItem = styled(Box)({
  minWidth: "110px",
  width: "110px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const MedalImage = styled("img")({
  width: "110px",
  height: "110px",
  maxHeight: "110px",
  objectFit: "contain",
  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
});

const MedalImageBlur = styled(MedalImage)({
  filter: 'blur(4px) grayscale(100%)',
})

const MAX_MEDALS_COMPLETED = 4
const MAX_MEDALS_IN_PROGRESS = 4

const RecentMedalsSection = ({ currentUser, currentUserQuery }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    isLoading: isLoadingMedals = false,
    isFetching: isFetchingMedals = false,
    isError: hasErrorMedals = false,
    refetch: refetchCurrentUser,
  } = currentUserQuery ?? {};
  const medals = currentUser?.medals;
  
  const handleViewAll = () => {
    navigate(routes.R_ACHIEVEMENTS);
  };

  const displayMedals = useMemo(() => {
    if (!Array.isArray(medals) || medals.length === 0) return []

    // Cria uma cópia antes de ordenar para não modificar o cache do TanStack Query.
    const sortedMedals = [...medals].sort((a, b) => {
      const dateA = new Date(a.date_received || 0).getTime();
      const dateB = new Date(b.date_received || 0).getTime();
      return dateB - dateA
    })
    const completedMedal = sortedMedals.filter(medals => {
      return medals["medal_complete"]
    }).slice(0, MAX_MEDALS_COMPLETED)

    const inProgressMedals = sortedMedals
      .map((medal) => {
        const total = medal.conditions.length
        const medalCount = medal.conditions.filter(condition => condition.is_complete).length
        const progress = total > 0 ? medalCount / total : 0
        return { ...medal, medalCount, total, progress }
      })
      .sort((a, b) => b.progress - a.progress)
      .filter(medal => !medal.medal_complete && medal.medalCount > 0).slice(0, MAX_MEDALS_IN_PROGRESS)

    return [...completedMedal, ...inProgressMedals]
  }, [medals])

  const renderLoadingState = () => (
    <MedalsGrid>
      {[...Array(MAX_MEDALS_COMPLETED)].map((_, index) => (
        <MedalItem key={index}>
          <Skeleton variant="circular" width={110} height={110} />
        </MedalItem>
      ))}
    </MedalsGrid>
  );

  const renderMedals = useMemo(() => {
    return (
      <MedalsGrid>
        {displayMedals.map((medal, index) => (
          <MedalItem key={medal.id || index}>
            {medal.medal_complete ? (
              <MedalImage
                src={`${API_Q_MEDALS_IMAGE}/${medal.image || medal.icon}`}
                alt={medal.name || `Medal ${index + 1}`}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <MedalImageBlur
                src={`${API_Q_MEDALS_IMAGE}/${medal.image || medal.icon}`}
                alt={medal.name || `Medal ${index + 1}`}
                onError={(e) => {
                  e.target.style.display = "none";
                }}

              />
            )}
          </MedalItem>
        ))}
      </MedalsGrid>
    )
  }, [displayMedals]);

  if (hasErrorMedals && displayMedals.length === 0) {
    return (
      <DashboardCard title={t("global.recent_medals")}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Typography color="text.secondary">
            {t("infos.unable_to_load_information_please_try_again_soon")}
          </Typography>
          <Button
            type="button"
            variant="outlined"
            disabled={isFetchingMedals}
            onClick={() => refetchCurrentUser?.()}
          >
            {t("global.try_again")}
          </Button>
        </Box>
      </DashboardCard>
    );
  }

  if (!isLoadingMedals && displayMedals.length === 0) return null;

  return (
    <DashboardCard
      title={t("global.recent_medals")}
      onArrowClick={handleViewAll}
      onClick={handleViewAll}
    >
      {isLoadingMedals ? renderLoadingState() : renderMedals}
    </DashboardCard>
  );
};

export default RecentMedalsSection;
